import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Checkbox } from "@/components/ui/checkbox";

type Method = "totp" | "backup";

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TwoFactorChallenge({ onSuccess, onCancel }: Props) {
  const [method, setMethod] = useState<Method>("totp");
  const [totpCode, setTotpCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const code = method === "totp" ? totpCode : backupCode.replace(/[-\s]/g, "").toUpperCase();
  const codeValid =
    method === "totp" ? totpCode.length === 6 : code.length === 10;

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!codeValid || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code, method, rememberDevice }),
      });

      if (res.ok) {
        onSuccess();
        return;
      }

      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        const until = data.lockedUntil
          ? new Date(data.lockedUntil).toLocaleTimeString()
          : "later";
        setError(`Too many attempts. Try again after ${until}.`);
      } else if (res.status === 401) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Invalid code. Try again.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } catch {
      setError("Network error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancel() {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "include" });
    } catch {
      // ignore — falls through to onCancel
    }
    onCancel();
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="bg-white dark:bg-gray-900 p-8 rounded shadow-md w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <ShieldCheck className="h-12 w-12 text-[#1737A2] dark:text-blue-400 mb-2" />
          <h2 className="text-2xl font-bold text-[#1737A2] dark:text-blue-400 mb-1">
            Two-Factor Authentication
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
            {method === "totp"
              ? "Enter the 6-digit code from your authenticator app."
              : "Enter one of your backup codes."}
          </p>
        </div>

        <div className="flex border rounded-md overflow-hidden mb-6">
          <button
            type="button"
            className={`flex-1 py-2 text-sm ${
              method === "totp"
                ? "bg-[#1737A2] text-white"
                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => {
              setMethod("totp");
              setError("");
            }}
          >
            Authenticator
          </button>
          <button
            type="button"
            className={`flex-1 py-2 text-sm ${
              method === "backup"
                ? "bg-[#1737A2] text-white"
                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => {
              setMethod("backup");
              setError("");
            }}
          >
            Backup code
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {method === "totp" ? (
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={setTotpCode}
                autoFocus
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          ) : (
            <input
              type="text"
              autoFocus
              autoCapitalize="characters"
              autoComplete="one-time-code"
              placeholder="XXXXX-XXXXX"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded px-3 py-2 font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-[#1737A2]"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value)}
            />
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Checkbox
              checked={rememberDevice}
              onCheckedChange={(v) => setRememberDevice(v === true)}
            />
            Remember this device
          </label>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <Button
            type="submit"
            variant="default"
            className="w-full bg-[#1737A2] hover:bg-[#102366]"
            disabled={!codeValid || submitting}
          >
            {submitting ? "Verifying..." : "Verify"}
          </Button>

          <button
            type="button"
            onClick={cancel}
            className="block mx-auto text-xs text-gray-500 hover:underline"
          >
            Use a different account
          </button>
        </form>
      </div>
    </div>
  );
}
